import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";
import { Turbo } from "@hotwired/turbo-rails";

// CodeMirror is large; lazy-import it so only the query editor page pays for it, not every admin page
// this auto-discovered controller is bundled into. Cached so it loads at most once per page.
let codeMirrorPromise;
const loadCodeMirror = () => {
  codeMirrorPromise ||= Promise.all([
    import("codemirror"),
    import("@codemirror/view"),
    import("@codemirror/lang-sql"),
    import("@codemirror/state"),
    import("@codemirror/autocomplete"),
    import("@codemirror/commands"),
  ]).then(([cm, view, langSql, state, autocomplete, commands]) => ({
    EditorView: cm.EditorView,
    basicSetup: cm.basicSetup,
    keymap: view.keymap,
    sql: langSql.sql,
    EditorState: state.EditorState,
    Prec: state.Prec,
    acceptCompletion: autocomplete.acceptCompletion,
    insertTab: commands.insertTab,
    insertNewline: commands.insertNewline,
  }));
  return codeMirrorPromise;
};

// Mounts a CodeMirror SQL editor over the plain textarea (which stays hidden and holds the value for
// form submit), and runs the query into the preview pane. Admin has Turbo Drive off, so preview uses
// request.js rather than a form submit.
export default class extends Controller {
  static targets = [
    "sql",
    "editor",
    "preview",
    "vizControl",
    "settingsPanel",
    "settingsToggle",
    "graphSettings",
    "seriesSettings",
    "tableSettings",
    "scalarSettings",
    "scalarHelp",
    "periodSettings",
    "colorSingle",
    "colorSeries",
    "metrics",
    "metricRow",
  ];
  static values = { url: String, maxSeries: Number };

  async connect() {
    this.sqlTarget.classList.add("hidden");
    const {
      EditorView,
      basicSetup,
      keymap,
      sql,
      EditorState,
      Prec,
      acceptCompletion,
      insertTab,
      insertNewline,
    } = await loadCodeMirror();
    // The controller may have disconnected while CodeMirror loaded.
    if (!this.element.isConnected) return;

    this.editor = new EditorView({
      parent: this.editorTarget,
      state: EditorState.create({
        doc: this.sqlTarget.value,
        extensions: [
          // Highest precedence so Mod-Enter runs (not a newline) and Tab accepts an open completion
          // before the default keymaps handle those keys. With no completion open, Tab inserts a tab at
          // the cursor (or indents a selection) rather than escaping focus.
          Prec.highest(
            keymap.of([
              {
                key: "Mod-Enter",
                preventDefault: true,
                run: () => (this.runFull(), true),
              },
              {
                key: "Shift-Mod-Enter",
                preventDefault: true,
                run: () => (this.save(), true),
              },
              {
                key: "Tab",
                preventDefault: true,
                run: (view) => acceptCompletion(view) || insertTab(view),
              },
              // Plain newline on Enter -- basicSetup's default copies the previous line's indent, which pastes
              // an unwanted leading tab onto each new line.
              { key: "Enter", run: insertNewline },
            ]),
          ),
          basicSetup,
          sql(),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              this.sqlTarget.value = update.state.doc.toString();
              this.dispatch("changed");
            }
          }),
          EditorView.theme({
            "&": { fontSize: "12px" },
            ".cm-scroller": { fontFamily: "ui-monospace, monospace" },
          }),
        ],
      }),
    });

    this.syncSettings();
  }

  // Handles the shortcuts when focus is outside the editor (e.g. on a button); the in-editor case is
  // covered by the CodeMirror keymap above, so ignore events originating inside it to avoid running twice.
  runShortcut(event) {
    if (this.editorTarget.contains(event.target)) return;
    if (event.key !== "Enter") return;

    // A bare Enter in a settings input would implicitly submit (save) the whole form, so swallow it --
    // only the ⌘⇧↵ chord saves. Covers text AND number inputs (the tick-step/target-value fields are
    // type=number and would otherwise submit). Enter just commits the field; blur triggers its change->run.
    if (!(event.metaKey || event.ctrlKey)) {
      if (!event.target.matches('input[type="text"], input[type="number"]'))
        return;
      event.preventDefault();
      event.target.blur();
      return;
    }

    event.preventDefault();
    event.shiftKey ? this.save() : this.runFull();
  }

  save() {
    // Save is a native form submit, which posts every field — including a hidden type section's controls
    // (e.g. the scalar block's value_format on a bar card), where a stale value would clobber the active
    // one via last-write-wins. Disabling them first drops them from the submission (disabled == not sent).
    this.vizControlTargets.forEach((control) => {
      if (this.inHiddenTypeSection(control)) control.disabled = true;
    });
    this.sqlTarget.form?.requestSubmit();
  }

  // Paint the opacity slider's filled portion as it drags. Only touches background-size (the CSS fill),
  // never the value attribute -- the raw 0.6..1.0 value is what posts as `opacity`.
  updateOpacityFill(event) {
    const el = event.target;
    const pct = ((el.value - el.min) / (el.max - el.min)) * 100;
    el.style.backgroundSize = `${pct}% 100%`;
  }

  // Show/hide the docked settings column without touching the chart, so the preview keeps its width when
  // the panel is closed.
  toggleSettings() {
    if (!this.hasSettingsPanelTarget) return;
    const hidden = this.settingsPanelTarget.classList.toggle("hidden");
    if (this.hasSettingsToggleTarget)
      this.settingsToggleTarget.classList.toggle("hidden", !hidden);
  }

  setValue(value) {
    this.editor.dispatch({
      changes: { from: 0, to: this.editor.state.doc.length, insert: value },
    });
    this.sqlTarget.value = value;
  }

  disconnect() {
    this.editor?.destroy();
  }

  reorderMetrics() {
    this.metricsChanged();
  }

  // Metric edits bypass the select-change wiring that normally marks the form dirty, so without this
  // the unsaved-changes guard stays clean and navigating away discards them silently.
  metricsChanged() {
    this.dispatch("changed");
    this.preview(true);
  }

  // Stacking needs multi-series data, so its toggle only shows once a series column is chosen.
  syncSettings() {
    const vizValue = (name) =>
      this.vizControlTargets.find((c) => c.name === name)?.value;
    const type = vizValue("chart_type");
    if (this.hasGraphSettingsTarget) {
      this.graphSettingsTarget.classList.toggle(
        "hidden",
        type !== "bar" && type !== "line",
      );
    }
    if (this.hasTableSettingsTarget) {
      this.tableSettingsTarget.classList.toggle("hidden", type !== "table");
    }
    if (this.hasScalarSettingsTarget) {
      this.scalarSettingsTarget.classList.toggle("hidden", type !== "scalar");
    }
    if (this.hasScalarHelpTarget) {
      this.scalarHelpTarget.classList.toggle("hidden", type !== "scalar");
    }
    // The polarity toggle only colors a period-over-period delta, so it's meaningful only once a period
    // column is chosen (no period -> no comparison -> nothing to color).
    if (this.hasPeriodSettingsTarget) {
      this.periodSettingsTarget.classList.toggle(
        "hidden",
        type !== "scalar" || !vizValue("period_column"),
      );
    }
    // Wide charts (y_columns) have per-series controls without a series_column. Read that off the
    // rendered rows rather than a page-level flag, so it survives the panel re-rendering after a Run.
    const hasSeries =
      !!vizValue("series_column") ||
      !!this.element.querySelector('input[name="series_types[][key]"]');
    if (this.hasSeriesSettingsTarget) {
      this.seriesSettingsTarget.classList.toggle("hidden", !hasSeries);
    }
    // Single color/opacity vs per-series swatches are mutually exclusive: the chart uses the series map
    // when split, the single color otherwise. Both stay in the DOM so their values keep posting.
    if (this.hasColorSingleTarget) {
      this.colorSingleTarget.classList.toggle("hidden", hasSeries);
    }
    if (this.hasColorSeriesTarget) {
      this.colorSeriesTarget.classList.toggle("hidden", !hasSeries);
    }
  }

  // Wide-format metrics. Rows are the source of truth -- each posts a y_columns[] entry -- and both
  // actions re-render the settings panel, so the per-series rows, the remove buttons and the "add"
  // link all follow the new set rather than going stale.
  addMetric() {
    const row = this.metricRowTargets[0];
    // Past the builder's cap every preview fails validation, so stop offering the action.
    if (!row || this.metricRowTargets.length >= this.maxSeriesValue) return;

    const charted = this.chartedMetrics();
    const unused = this.metricOptions(row).find(
      (value) => !charted.includes(value),
    );
    if (!unused) return;

    const added = row.cloneNode(true);
    this.setMetricRowValue(added, unused);
    this.metricsTarget.append(added);
    this.metricsChanged();
  }

  // A chart with no metrics can't render, so the last row stays.
  removeMetric(event) {
    if (this.metricRowTargets.length <= 1) return;

    event.target
      .closest("[data-admin--report--edit-card-query-target='metricRow']")
      .remove();
    this.metricsChanged();
  }

  chartedMetrics() {
    return this.metricRowTargets.map(
      (row) => row.querySelector('input[name="y_columns[]"]').value,
    );
  }

  metricOptions(row) {
    return [...row.querySelectorAll("[data-select-target='option']")]
      .map((option) => option.dataset.selectValue)
      .filter(Boolean);
  }

  // Set before the row is inserted: the select controller renders its own display and checkmark on
  // connect, so hand-syncing those nodes here would just be overwritten -- or worse, left stale.
  setMetricRowValue(row, value) {
    row.querySelector('input[name="y_columns[]"]').value = value;
    const select = row.querySelector("[data-controller='select']");
    if (select) select.dataset.selectSelectedValue = value;
  }

  // A viz-control change: re-render only the preview (leave the open settings popover untouched).
  run() {
    this.preview(false);
  }

  // Explicit Run: also re-render the settings so the column pickers reflect the new result's columns.
  runFull() {
    this.preview(true);
  }

  // Series column changed: re-render the settings too, so the per-series color/label rows rebuild for the
  // new series. The SQL is unchanged, so the preview reuses the cached rows -- no Snowflake re-query.
  runSeriesChange() {
    this.preview(true);
  }

  async preview(refreshSettings) {
    this.syncSettings();
    // Tag each run so a slow earlier preview that resolves last can't overwrite a newer one.
    const runId = (this.latestRun = (this.latestRun || 0) + 1);
    this.previewTarget.innerHTML =
      '<div class="flex flex-1 items-center justify-center">' +
      '<div class="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-500"></div>' +
      "</div>";
    // FormData (not a plain object) so bracketed names like column_labels[revenue] are form-encoded and
    // Rails re-nests them into params[:column_labels]; a JSON object would post the literal bracket key.
    const body = new FormData();
    body.append("sql", this.sqlTarget.value);
    if (refreshSettings) body.append("refresh_settings", "1");
    this.vizControlTargets.forEach((control) => {
      // Skip controls in a hidden chart-type section (the scalar/table block on a bar card, etc.) so a
      // hidden control sharing a name with the active one (both "value_format") can't clobber it via
      // last-write-wins. Scoped to the top-level type sections only — nested sub-toggles like colorSingle
      // are intentionally hidden-but-posting, so a bare `.hidden` check would wrongly drop them.
      if (this.inHiddenTypeSection(control)) return;
      // Match native form submission: an unchecked checkbox posts nothing, so its param is absent.
      if (control.type === "checkbox" && !control.checked) return;
      body.append(control.name, control.value);
    });
    // The controller returns the stream tags as text/html (not text/vnd.turbo-stream.html) so request.js
    // does NOT auto-render them inside post() -- that would fire before the runId guard and let a stale run
    // overwrite a newer one. We read the body and render it manually only past the guard.
    const response = await post(this.urlValue, { body });
    if (runId !== this.latestRun) return;
    if (response.ok) {
      Turbo.renderStreamMessage(await response.text);
      this.syncSettings();
    } else {
      this.previewTarget.innerHTML =
        '<div class="flex flex-1 items-center justify-center px-4 text-center text-sm text-muted-foreground">' +
        "Something went wrong running the preview. Try again." +
        "</div>";
    }
  }

  // True when the control lives inside a hidden top-level chart-type section (graph/table/scalar). Nested
  // sub-toggles (colorSingle, seriesSettings, periodSettings) are deliberately excluded — they can be
  // hidden while their type section is active and must still post.
  inHiddenTypeSection(control) {
    const section = control.closest(
      "[data-admin--report--edit-card-query-target~='graphSettings']," +
        "[data-admin--report--edit-card-query-target~='tableSettings']," +
        "[data-admin--report--edit-card-query-target~='scalarSettings']",
    );
    return !!section && section.classList.contains("hidden");
  }
}
